import {
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { shallowEqual } from 'react-redux'
import moment from 'moment'
import { v4 as uuid } from 'uuid'
import check from '@assets/images/check.svg'
import filter from '@assets/images/filter.svg'
import { useFetchSearchFiltersQuery, useUpdateSearchFilterMutation } from '@entities/search'
import { Chip } from '@microservices/wiskey-react-components'
import { ArrowDropDown } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  ClickAwayListener,
  Divider,
  ListItemButton,
  ListItemButtonProps,
  ListItemText,
  Popper,
  Tooltip,
  Typography,
} from '@mui/material'
import { grey, red } from '@mui/material/colors'
import Grid from '@mui/material/Grid/Grid'
import { SearchAssistantStoredSearchFiltersMapper } from '@widgets/SearchAssistant/model/SearchAssistantStoredSearchFiltersMapper'
import { SearchAssistantPresenter } from '@widgets/SearchAssistant/presenter/search-assistant.presenter'
import {
  getCriterias,
  getDefaultSearchFilter,
  getSelectedFavouriteCriteria,
} from '@widgets/SearchAssistant/store/search-assistant.slice'

import { truncateDateTimeToMinute } from '@pages/ConfiguredEntity/components/SearchAssistant/helpers'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { AppLoader } from '@components/AppLoader'
import { CustomSvgIcon } from '@components/CustomSvgIcon'

import {
  useFetchAllCriteriasQuery,
  useFetchDefaultViewSAQuery,
} from '@redux/api'
import { openSavedFiltersDialog } from '@redux/reducers/dialogWindowManager.reducer'

import { useAppDispatch, useAppSelector, useKeyPress } from '@hooks'
import {
  buttonStylesByMode,
  checkDirtyStoredValue,
  filterAndSortCriteriasByOrder,
  getInitialPositionWindow,
  getSortParamRequest,
} from '@helpers'
import {
  CriteriaType,
  FAVORITES_FILTERS_BUTTON_CLASSNAME,
  FIELD_VALUE_FORMAT,
  FILTERS_SA_TYPE,
  HOT_KEY,
  SEARCH_FIELD_TYPE,
  SORT_BY_ORDER_AND_DEFAULT,
  ValueAddType,
} from '@constants'
import { AutocompleteOption, DIALOG_WINDOW_TYPE, GETSearchType, GETStoredValueType } from '@types'

import { SearchAssistantProvider } from '../../context'
import { SearchAssistantAccordion } from '../SearchAssistantAccordion'
import { FavoritesFiltersTableMode } from '../SearchAssistantFilterManager'
import { RelativeType } from '../SearchAssistantRelativeDatePicker/SearchAssistantRelativeDatePicker'

import { mapStoredSearchFilterToCriteria } from './mapStoredSearchFilterToCriteria'
import { PinnedCriteriaFields } from './PinnedCriteriaFields'
import { UnpinnedCriteriaFields } from './UnpinnedCriteriaFields'

export const SearchAssistantConsumer = {
  dialogWindow: 'dialogWindow',
  page: 'page',
} as const

const SearchAssistantCriteriasEmptyState = () => {
  const { t } = useTranslation()

  return (
    <Grid
      item
      data-component='SearchAssistantCriteriasEmptyState'
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 100,
      }}
    >
      <Typography mb={2} ml={2}>
        {t('searchFilterForm.emptyCriterias')}
      </Typography>
    </Grid>
  )
}

type ListItemProps = { title: string } & ListItemButtonProps

const ListItem: React.FC<ListItemProps> = props => {
  const { selected, title, ...rest } = props

  return (
    <ListItemButton
      sx={{
        p: '0 50px',
        height: 24,
        display: 'flex',
        '&:hover': {
          bgcolor: theme => theme.palette.background.hovered,
          textOverflow: 'ellipsis',
          overflow: 'hidden',
        },
        '&.Mui-selected': {
          bgcolor: theme => theme.palette.background.hovered,
        },
      }}
      {...rest}
    >
      {selected && (
        <Grid item alignSelf={'end'} left={15} position={'absolute'}>
          <CustomSvgIcon imgStyle={{}} src={check} />
        </Grid>
      )}
      <ListItemText
        title={title}
        sx={{
          m: 0,
          '& .MuiTypography-root': {
            fontSize: 13,
            lineHeight: '24px',
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            fontFamily: 'Montserrat Regular',
            color: theme => theme.palette.color.listItem,
            whiteSpace: 'nowrap',
          },
        }}
      >
        {title}
      </ListItemText>
    </ListItemButton>
  )
}

const getValueFormat = (value: GETStoredValueType) => {
  const parsedValue = value.value.length ? JSON.parse(value.value) : value.value

  if (value.valueObject.valueFormat === FIELD_VALUE_FORMAT.BOOLEAN) {
    if (parsedValue === 'false') {
      return false
    }
    if (parsedValue === 'true') {
      return true
    }
  }

  if (value.valueObject.valueFormat === FIELD_VALUE_FORMAT.NUMBER) {
    if (parsedValue === '1') {
      return 1
    }
    if (parsedValue === '0') {
      return 0
    }
  }

  return parsedValue
}

function swap<T>(array: T[], index1: number, index2: number): T[] {
  const newArray = [...array] // копия, чтобы не мутировать исходный
  ;[newArray[index1], newArray[index2]] = [newArray[index2], newArray[index1]]

  return newArray
}

export type SearchAssistantConsumerType =
  (typeof SearchAssistantConsumer)[keyof typeof SearchAssistantConsumer]

interface SearchAssistantProps {
  children?: React.ReactNode
  loading?: boolean
  contextId?: string | number
  dialogId?: string
  consumer: SearchAssistantConsumerType
  entityObjectCode: string | undefined
  entityInternalId: string | undefined
  hasActualTimeWindow: boolean
  searchAssistantId?: string | number
  visible?: boolean
  onSearchFilter: (criteria: CriteriaType[], isUpdateFiltersWithEmptyDefaults?: boolean) => void
  onReset?: () => void
  onLoadSavedSearchFilters?: (loading: boolean) => void
}

export interface SearchAssistantRef {
  clearCriterias: () => void
}

const SearchAssistant = forwardRef<SearchAssistantRef, SearchAssistantProps>(
  (props, forwardedRef) => {
    const {
      children,
      loading = false,
      contextId,
      dialogId,
      consumer,
      searchAssistantId = '',
      entityObjectCode,
      entityInternalId,
      hasActualTimeWindow,
      visible,
      onSearchFilter,
      onReset,
    } = props

    const instanceId = useId()

    const { t } = useTranslation()

    const searchAssistantKey = useMemo(() => {
      let key: string | undefined = `${consumer}`

      if (dialogId) {
        key = `${dialogId}:${consumer}`
      }

      return key
    }, [dialogId, consumer])

    //#region Criteria fields

    const searchAssistantPresenter = useMemo(
      () => new SearchAssistantPresenter(searchAssistantKey),
      [searchAssistantKey]
    )

    const {
      data: { data: criterias = [] } = {},
      isLoading: isLoadingCriterias,
      isFetching: isFetchingCriterias,
    } = useFetchAllCriteriasQuery(
      {
        searchAssistantId,
        objectCode: entityObjectCode,
        sort: getSortParamRequest(SORT_BY_ORDER_AND_DEFAULT),
        instanceId,
      },
      {
        skip: !entityObjectCode || !searchAssistantId,
        refetchOnMountOrArgChange: true,
      }
    )

    const isPendingCriterias = isLoadingCriterias || isFetchingCriterias

    const {
      data: savedSearchFilters,
      isLoading: isLoadingSearchFilters,
      isFetching: isFetchingSearchFilters,
    } = useFetchSearchFiltersQuery(
      {
        objectCode: entityObjectCode,
        searchAssistantId,
        instanceId,
      },
      {
        skip: !entityObjectCode || !searchAssistantId,
        refetchOnMountOrArgChange: true,
      }
    )

    const isPendingSearchFilters = isLoadingSearchFilters || isFetchingSearchFilters

    // Инициализация критериев поиска
    useEffect(() => {
      searchAssistantPresenter.saveCriterias(filterAndSortCriteriasByOrder(criterias))
    }, [criterias])

    // Критерии, которые требуются для манипулирования ими только во время жизни приложения
    // Актуальные, то есть единственный источник правды приходит из query хука и называется criterias
    const criteriasModel = useAppSelector(
      state => getCriterias(state, searchAssistantKey),
      shallowEqual
    )

    const visiblePinnedCriterias = useMemo(
      () => searchAssistantPresenter.getVisiblePinnedCriteriaFields(criteriasModel),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [criteriasModel]
    )

    const visibleUnpinnedCriterias = useMemo(
      () => searchAssistantPresenter.getVisibleUnpinnedCriteriaFields(criteriasModel),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [criteriasModel]
    )

    //#endregion

    //#region Search filter

    const defaultSearchFilter = useAppSelector(
      state => getDefaultSearchFilter(state, searchAssistantKey),
      shallowEqual
    )

    useEffect(() => {
      if (Array.isArray(savedSearchFilters)) {
        const defaultSearchFilter = savedSearchFilters.find(filter => filter.defaultFilter) ?? null

        searchAssistantPresenter.saveDefaultSearchFilter(defaultSearchFilter)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedSearchFilters])

    //#endregion

    useEffect(() => {
      return () => {
        searchAssistantPresenter.disposeSearchAssistant()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchAssistantKey])

    const { isLoading: isLoadingDefaultViewSA } = useFetchDefaultViewSAQuery(
      { contextId },
      { skip: !contextId }
    )

    const isLoadingContextViews = false

    const skipSearchByEnter = useRef(true)

    const isLoadingSearchAssistant =
      loading || isLoadingDefaultViewSA || isLoadingContextViews || isPendingCriterias

    const isInDialogWindow = useMemo(
      () => consumer === SearchAssistantConsumer.dialogWindow,
      [consumer]
    )

    const [isOpenFilter, setIsOpenFilter] = useState(() => isInDialogWindow)
    const [isDropDownWindowOpen, setDropDownWindowOpen] = useState(false)

    useLayoutEffect(() => {
      setIsOpenFilter(isInDialogWindow || (!!searchAssistantId && !!defaultSearchFilter))
    }, [isInDialogWindow, searchAssistantId, defaultSearchFilter])

    const handleOpenFilter = () => setIsOpenFilter(prev => !prev)

    useImperativeHandle(forwardedRef, () => {
      return {
        clearCriterias() {
          const initialVisibleCriterias = filterAndSortCriteriasByOrder(criterias).filter(
            criteria => criteria.visibility
          )

          searchAssistantPresenter.saveCriterias([])

          if (selectedFavouriteCriteria) {
            searchAssistantPresenter.saveSelectedFavouriteCriteria(null)
          }

          if (selectedCriteriaChips.length) {
            setSelectedCriteriaChips([])
          }
        },
      }
    })

    const handleUpdateAutocompleteVisibleCriteria = (
      replacedCriteria: CriteriaType,
      newValue: AutocompleteOption
    ) => {
      const newCriteriaIdx = criteriasModel.findIndex(criteria => criteria.title === newValue.label)
      const replacedCriteriaIdx = criteriasModel.findIndex(
        criteria => criteria.title === replacedCriteria.title
      )

      searchAssistantPresenter.saveCriterias(
        swap(criteriasModel, newCriteriaIdx, replacedCriteriaIdx)
      )
    }

    const handleChangeCriteriaValue = (criteriaValue: ValueAddType) => {
      const { codeCriteria, valueId, searchRule, value, dailyRange } = criteriaValue

      const updatedCriterias = criteriasModel.map(criteria => {
        // находим текущий критерий
        if (criteria.codeCriteria === codeCriteria) {
          return {
            ...criteria,
            storedValues: criteria.storedValues.map(storedValue => {
              if (valueId) {
                //если id сохраненных полей совпадает, то обновить текущее значение
                if (storedValue.id === valueId) {
                  return { ...storedValue, searchRule, value, dailyRange }
                }

                return storedValue
              }

              return {
                ...storedValue,
                searchRule,
                value,
              }
            }),
          }
        }

        return criteria
      })

      searchAssistantPresenter.saveCriterias(updatedCriterias)

      setFirstRelativeValueCodeCriteria(null)

      // Зачем это условие? С ним чипсы не очищались
      // if (!value) {
      //   return
      // }

      const updatedSelectedCriteriaChip = criterias.find(
        criteria => criteria.codeCriteria === codeCriteria
      )

      const updatedCriteria = updatedCriterias.find(
        criteria => criteria.codeCriteria === codeCriteria
      )

      if (updatedSelectedCriteriaChip && updatedCriteria) {
        setSelectedCriteriaChips(prevSelectedCriteriaChips => {
          const existingIndex = prevSelectedCriteriaChips.findIndex(
            chip => chip.codeCriteria === updatedCriteria.codeCriteria
          )

          if (existingIndex !== -1) {
            // Обновить существующий
            const newChips = [...prevSelectedCriteriaChips]

            // Проверяем, есть ли хотя бы одно "грязное" значение (которое должно отображаться как чип)
            const hasDirtyStoredValue = updatedCriteria.storedValues.some(storedValue =>
              checkDirtyStoredValue(storedValue.value, storedValue.searchRule)
            )

            // Если нет "грязных" значений, то удаляем этот chip
            if (!hasDirtyStoredValue) {
              newChips.splice(existingIndex, 1)

              return newChips
            }

            newChips[existingIndex] = updatedCriteria

            return newChips
          } else {
            // Добавить новый только если есть "грязные" значения
            const hasDirtyStoredValue = updatedCriteria.storedValues.some(storedValue =>
              checkDirtyStoredValue(storedValue.value, storedValue.searchRule)
            )

            if (hasDirtyStoredValue) {
              return [...prevSelectedCriteriaChips, updatedCriteria]
            } else {
              return prevSelectedCriteriaChips
            }
          }
        })
      }
    }

    //#region Favourites dialog

    const anchorRef = useRef<HTMLButtonElement | null>(null)

    const [isOpenFavouritesDialog, setIsOpenFavouritesDialog] = useState(false)

    const handleToggleFavouritesDialog = () => setIsOpenFavouritesDialog(prev => !prev)

    const [updateSearch, { isLoading: isUpdatingSearchFilters }] = useUpdateSearchFilterMutation()

    const selectedFavouriteCriteria = useAppSelector(
      state => getSelectedFavouriteCriteria(state, searchAssistantKey),
      shallowEqual
    )

    useEffect(() => {
      if (defaultSearchFilter) {
        searchAssistantPresenter.saveSelectedFavouriteCriteria(defaultSearchFilter)
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultSearchFilter])

    const handleSelected = (value: GETSearchType) => () => {
      // const updatedVisibleCriterias = filterAndSortCriteriasByOrder(
      //   criterias.filter(criteria => criteria.visibility)
      // ).map(prevVisibleCriteria => {
      //   const dirtyInPrev = prevVisibleCriteria.storedValues.some(storedValue =>
      //     checkDirtyStoredValue(storedValue.value, storedValue.searchRule)
      //   )

      //   if (!dirtyInPrev) return prevVisibleCriteria

      //   // Найти исходный критерий из загруженных criterias
      //   const original = criterias?.find(c => c.codeCriteria === prevVisibleCriteria.codeCriteria)

      //   if (original) {
      //     // Клонируем оригинал, чтобы не мутировать shared объекты
      //     return { ...original, storedValues: original.storedValues.map(sv => ({ ...sv })) }
      //   }

      //   // Если оригинал не найден — делаем безопасный сброс значений
      //   return {
      //     ...prevVisibleCriteria,
      //     storedValues: prevVisibleCriteria.storedValues.map(storedValue => ({
      //       ...storedValue,
      //       value: storedValue.valueType === FIELD_VALUE_TYPE.DATETIME ? null : '',
      //       searchRule: FILTERS_SA_TYPE.EQUAL,
      //     })),
      //   }
      // })

      searchAssistantPresenter.saveSelectedFavouriteCriteria(value)

      handleToggleFavouritesDialog()
    }

    const isDirtyVisibleCriterias = useMemo(() => {
      return (
        criteriasModel?.some(visibleCriteria => {
          return visibleCriteria.storedValues.some(storedValue => {
            return checkDirtyStoredValue(storedValue.value, storedValue.searchRule)
          })
        }) || false
      )
    }, [criteriasModel])

    const handleClickSave = () => {
      if (selectedFavouriteCriteria?.id) {
        updateSearch({
          title: selectedFavouriteCriteria.title,
          searchAssistantId,
          objectCode: entityObjectCode,
          storedSearchFilters: SearchAssistantStoredSearchFiltersMapper.toDto(criteriasModel),
        })
          .unwrap()
          .then(() => handleToggleFavouritesDialog())
      }
    }

    const dispatch = useAppDispatch()

    const handleClickSaveAs = (event: React.MouseEvent<HTMLDivElement>) => {
      const initialPosition = getInitialPositionWindow(event, { width: 320, height: 290 })

      if (entityObjectCode && searchAssistantKey) {
        const newSearchFilter = {
          searchAssistantId,
          objectCode: entityObjectCode,
          storedSearchFilters: SearchAssistantStoredSearchFiltersMapper.toDto(criteriasModel),
        }

        handleToggleFavouritesDialog()
        dispatch(
          // TODO(isilanov): отказаться от управления модальными окнами через redux
          openSavedFiltersDialog({
            id: uuid(),
            parentDialogId: null,
            type: DIALOG_WINDOW_TYPE.SAVED_FILTERS,
            title: t('filter.favorites.dialog.title'),
            initialPosition,
            initialSize: { height: 290, width: 320 },
            minWidth: 310,
            icon: filter,
            meta: {
              mode: FavoritesFiltersTableMode.CREATE,
              entityObjectCode,
              searchAssistantId,
              searchAssistantKey,
              newSearch: { ...newSearchFilter, title: '' },
            },
          })
        )
      }
    }

    const handleClickManage = (event: React.MouseEvent<HTMLDivElement>) => {
      const initialPosition = getInitialPositionWindow(event, { width: 320, height: 290 })

      if (entityObjectCode && searchAssistantKey) {
        handleToggleFavouritesDialog()
        dispatch(
          openSavedFiltersDialog({
            id: uuid(),
            parentDialogId: null,
            type: DIALOG_WINDOW_TYPE.SAVED_FILTERS,
            title: t('filter.favorites.dialog.title'),
            icon: filter,
            initialPosition,
            initialSize: { height: 290, width: 320 },
            minWidth: 310,
            meta: {
              mode: FavoritesFiltersTableMode.VIEW,
              entityObjectCode,
              searchAssistantId,
              searchAssistantKey,
            },
          })
        )
      }
    }

    const handleReset = () => {
      if (selectedFavouriteCriteria) {
        searchAssistantPresenter.saveSelectedFavouriteCriteria(null)
      }

      const initialVisibleCriterias = filterAndSortCriteriasByOrder(criterias).filter(
        criteria => criteria.visibility
      )

      searchAssistantPresenter.saveCriterias(initialVisibleCriterias)

      setSelectedCriteriaChips([])

      onReset?.()
    }

    const handleClickAway = () => {
      skipSearchByEnter.current = true
    }

    const handleClickInside = () => {
      skipSearchByEnter.current = false
    }

    const { keyPressed: isStartSearch } = useKeyPress(
      HOT_KEY.ENTER,
      undefined,
      isDropDownWindowOpen,
      undefined,
      skipSearchByEnter
    )

    const [firstRelativeValueCodeCriteria, setFirstRelativeValueCodeCriteria] = useState<
      string | null
    >(null)

    const handleSearch = () => {
      const updatedVisibleCriterias: CriteriaType[] = JSON.parse(JSON.stringify(criteriasModel))
      let firstRelativeValueCriteria: string | null = null

      updatedVisibleCriterias.forEach(data => {
        data.storedValues.forEach(storedValue => {
          const value = storedValue.value

          if (storedValue.fieldType === SEARCH_FIELD_TYPE.RELATIVE_VALUE) {
            if (Array.isArray(value)) {
              storedValue.value = [
                value?.[0] || truncateDateTimeToMinute(moment()),
                value?.[1] || (storedValue.min || -24).toString(),
                value?.[2] || (storedValue.max || 48).toString(),
              ] as RelativeType

              if (firstRelativeValueCriteria === null) {
                firstRelativeValueCriteria = data.codeCriteria
                setFirstRelativeValueCodeCriteria(firstRelativeValueCriteria)
              }
            }
          }
        })
      })

      searchAssistantPresenter.saveCriterias(updatedVisibleCriterias)
      onSearchFilter(updatedVisibleCriterias)
    }

    useEffect(() => {
      if (isStartSearch) {
        handleSearch()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isStartSearch])

    const { keyPressed: isResetFilters } = useKeyPress(
      HOT_KEY.ESCAPE,
      undefined,
      isDropDownWindowOpen
    )

    useEffect(() => {
      if (isResetFilters && isDirtyVisibleCriterias) {
        onReset?.()
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isResetFilters])

    //#endregion

    //#region Chips

    const [selectedCriteriaChips, setSelectedCriteriaChips] = useState<CriteriaType[]>([])

    // Реакция на применение избранного фильтра
    useEffect(() => {
      if (!selectedFavouriteCriteria) {
        if (defaultSearchFilter === null) {
          onSearchFilter([], true)

          return
        }

        return
      }

      const storedSearchFiltersAsCriterias = selectedFavouriteCriteria.storedSearchFilters.map(
        mapStoredSearchFilterToCriteria
      )

      setSelectedCriteriaChips(storedSearchFiltersAsCriterias)

      const updatedCriterias = criterias.map(criteria => {
        const storedSearchFilterAsCriteria = storedSearchFiltersAsCriterias.find(
          storedSearchFiltersAsCriteria =>
            storedSearchFiltersAsCriteria.codeCriteria === criteria.codeCriteria
        )

        if (storedSearchFilterAsCriteria) {
          return storedSearchFilterAsCriteria
        }

        return criteria
      })

      const filteredCriterias = filterAndSortCriteriasByOrder(updatedCriterias)
      searchAssistantPresenter.saveCriterias(filteredCriterias)
      onSearchFilter(filteredCriterias)
    }, [selectedFavouriteCriteria, defaultSearchFilter])

    const handleChangeLastVisibleCriteria = (savedCriteria: CriteriaType, pinned: boolean) => {
      const newCriteriaIdx = criteriasModel.findIndex(
        criteria => criteria.title === savedCriteria.title
      )

      const lastVisibleCriteria = pinned
        ? visiblePinnedCriterias.at(-1)
        : visibleUnpinnedCriterias.at(-1)

      const replacedCriteriaIdx = criteriasModel.findIndex(
        criteria => criteria.title === lastVisibleCriteria?.title
      )

      searchAssistantPresenter.saveCriterias(
        swap(criteriasModel, newCriteriaIdx, replacedCriteriaIdx)
      )
    }

    const handleSelectCriteriaChip = (codeCriteria: string, pinned: boolean) => () => {
      const hasInVisibleCriterias = (
        pinned ? visiblePinnedCriterias : visibleUnpinnedCriterias
      ).some(visibleCriteria => visibleCriteria?.codeCriteria === codeCriteria)

      if (!hasInVisibleCriterias && selectedFavouriteCriteria) {
        const savedSearchFilterCriterias = selectedFavouriteCriteria.storedSearchFilters.map(
          storedSearchFilter => ({
            codeCriteria: storedSearchFilter.searchFilterObject.code,
            title: storedSearchFilter.searchFilterObject.title,
            id: storedSearchFilter.searchFilterObject.id,
            isPinning: storedSearchFilter.searchFilterObject.isPinning,
            order: storedSearchFilter.searchFilterObject.order,
            visibility: storedSearchFilter.searchFilterObject.visibility,
            storedValues: storedSearchFilter.storedValues.map(value => ({
              ...value.valueObject,
              value: getValueFormat(value),
              searchRule: value.searchRule,
            })),
          })
        ) as CriteriaType[]

        const savedSearchFilterCriteria = savedSearchFilterCriterias.find(
          savedSearchFilterCriteria => savedSearchFilterCriteria.codeCriteria === codeCriteria
        )

        if (savedSearchFilterCriteria) {
          handleChangeLastVisibleCriteria(savedSearchFilterCriteria, pinned)
        }
      }
    }

    const handleDeleteCriteriaChip = (criteriaType: CriteriaType) => () => {
      handleChangeCriteriaValue({
        codeCriteria: criteriaType.codeCriteria,
        value: '',
        searchRule: FILTERS_SA_TYPE.EQUAL,
      })

      const updatedSelectedCriteriaChips = selectedCriteriaChips.filter(
        selectedCriteriaChip => selectedCriteriaChip.codeCriteria !== criteriaType.codeCriteria
      )

      setSelectedCriteriaChips(updatedSelectedCriteriaChips)
    }

    const selectedPinnedCriteriaChipElements = selectedCriteriaChips
      .filter(criteria => criteria.isPinning)
      .map(selectedPinnedCriteriaChip => {
        const isVisibleFilter = criteriasModel.some(
          criteria => criteria?.codeCriteria === selectedPinnedCriteriaChip.codeCriteria
        )

        const hasRelativeValueField = selectedPinnedCriteriaChip.storedValues?.some(
          storedValue => storedValue.fieldType === SEARCH_FIELD_TYPE.RELATIVE_VALUE
        )

        const shouldColorRed =
          hasRelativeValueField &&
          firstRelativeValueCodeCriteria !== null &&
          selectedPinnedCriteriaChip.codeCriteria !== firstRelativeValueCodeCriteria

        const chipElement = (
          <Chip
            key={selectedPinnedCriteriaChip.codeCriteria}
            label={selectedPinnedCriteriaChip.title}
            size='small'
            variant={isVisibleFilter ? 'filled' : 'outlined'}
            sx={{
              height: isInDialogWindow ? '19px' : '23px',
              ml: 1,
              color: theme => (shouldColorRed ? red[500] : theme.palette.color.default),
              bgcolor: theme => theme.palette.background.chip,
              border: theme => theme.palette.border.field,
              '&.MuiChip-deleteIcon': {
                color: theme => (shouldColorRed ? red[500] : theme.palette.color.default),
              },
              '&.MuiChip-root:hover': {
                bgcolor: theme => theme.palette.background.hoveredChip,
              },
            }}
            onClick={handleSelectCriteriaChip(selectedPinnedCriteriaChip.codeCriteria, true)}
            onDelete={handleDeleteCriteriaChip(selectedPinnedCriteriaChip)}
          />
        )

        // div - костыль, т.к. тултип не хочет показываться на чипе, проблема в MUI
        return shouldColorRed ? (
          <Tooltip arrow sx={{ p: 0, m: 0 }} title={t('searchAssistant.oneRelative')}>
            <div style={{ display: 'flex' }}>{chipElement}</div>
          </Tooltip>
        ) : (
          chipElement
        )
      })

    const selectedUnpinnedCriteriaChipElements = selectedCriteriaChips
      .filter(criteria => !criteria.isPinning)
      .sort((a, b) => criteriasModel.findIndex(filter => filter.codeCriteria === b.codeCriteria))
      .map(selectedUnpinnedCriteriaChip => {
        const isVisibleFilter = criteriasModel.some(
          criteria => criteria?.codeCriteria === selectedUnpinnedCriteriaChip.codeCriteria
        )

        const hasRelativeValueField = selectedUnpinnedCriteriaChip.storedValues?.some(
          storedValue => storedValue.fieldType === SEARCH_FIELD_TYPE.RELATIVE_VALUE
        )

        const shouldColorRed =
          hasRelativeValueField &&
          firstRelativeValueCodeCriteria !== null &&
          selectedUnpinnedCriteriaChip.codeCriteria !== firstRelativeValueCodeCriteria

        const chipElement = (
          <Chip
            key={selectedUnpinnedCriteriaChip.codeCriteria}
            label={selectedUnpinnedCriteriaChip.title}
            size='small'
            variant={isVisibleFilter ? 'filled' : 'outlined'}
            sx={{
              height: isInDialogWindow ? '19px' : '23px',
              ml: 1,
              color: theme => (shouldColorRed ? red[500] : theme.palette.color.default),
              bgcolor: theme => theme.palette.background.chip,
              border: theme => theme.palette.border.field,
              '&.MuiChip-deleteIcon': {
                color: theme => (shouldColorRed ? red[500] : theme.palette.color.default),
              },
              '&.MuiChip-root:hover': {
                bgcolor: theme => theme.palette.background.hoveredChip,
              },
            }}
            onClick={handleSelectCriteriaChip(selectedUnpinnedCriteriaChip.codeCriteria, false)}
            onDelete={handleDeleteCriteriaChip(selectedUnpinnedCriteriaChip)}
          />
        )

        // div - костыль, т.к. тултип не хочет показываться на чипе, проблема в MUI
        return shouldColorRed ? (
          <Tooltip arrow sx={{ p: 0, m: 0 }} title={t('searchAssistant.oneRelative')}>
            <div style={{ display: 'flex' }}>{chipElement}</div>
          </Tooltip>
        ) : (
          chipElement
        )
      })

    //#endregion

    const getContent = () => {
      if (isLoadingSearchAssistant) {
        return (
          <Box alignItems='center' display='flex' height={100} justifyContent='center'>
            <CircularProgress size={32} />
          </Box>
        )
      }

      if (!criteriasModel.length) {
        return <SearchAssistantCriteriasEmptyState />
      }

      return (
        <Grid
          container
          bgcolor={theme => theme.palette.background.filter}
          direction={'column'}
          pb={1}
          px={1}
        >
          {visiblePinnedCriterias.length > 0 && (
            <Grid container>
              <Grid item mr={8}>
                <Typography color={grey[600]}>{t('filter.pinned')}</Typography>
              </Grid>
              <Grid alignItems='center' display='flex' sx={{ pl: 1 }}>
                {selectedPinnedCriteriaChipElements}
              </Grid>
            </Grid>
          )}
          <PinnedCriteriaFields
            disabled={isUpdatingSearchFilters}
            searchAssistantPresenter={searchAssistantPresenter}
            visibleCriterias={criteriasModel}
            onUpdateAutocomplete={handleUpdateAutocompleteVisibleCriteria}
            onUpdateCriteriaValue={handleChangeCriteriaValue}
          />
          {visibleUnpinnedCriterias.length > 0 && (
            <Grid container>
              <Grid item mr={8}>
                <Typography color={grey[600]}>{t('filter.chipsDesc')}</Typography>
              </Grid>
              <Grid alignItems='center' display='flex' sx={{ pl: 1 }}>
                {selectedUnpinnedCriteriaChipElements}
              </Grid>
            </Grid>
          )}
          <UnpinnedCriteriaFields
            // Используем стабильные критерии, которые не подвержены изменениям во время работы с фильтрами
            criterias={criterias}
            disabled={isUpdatingSearchFilters}
            searchAssistantPresenter={searchAssistantPresenter}
            visibleCriterias={criteriasModel}
            onUpdateAutocomplete={handleUpdateAutocompleteVisibleCriteria}
            onUpdateCriteriaValue={handleChangeCriteriaValue}
          />
          <Grid container alignItems='center' flexWrap='nowrap' justifyContent='space-between'>
            {hasActualTimeWindow && (
              <Grid item>
                <Chip
                  label={t('filter.activatedTimeWindow')}
                  size='small'
                  variant='filled'
                  sx={{
                    height: isInDialogWindow ? '19px' : '23px',
                    ml: 1,
                  }}
                />
              </Grid>
            )}
            <Grid container item gap={2} justifyContent='flex-end' mt={0.5} pr={3.1}>
              <Grid item flexDirection='column' sx={{ position: 'relative' }}>
                <Button
                  ref={anchorRef}
                  className={FAVORITES_FILTERS_BUTTON_CLASSNAME}
                  endIcon={<ArrowDropDown />}
                  sx={theme => buttonStylesByMode(theme, true)}
                  variant='text'
                  onClick={handleToggleFavouritesDialog}
                >
                  {t('filter.favorites.button')}
                </Button>
                <Popper
                  anchorEl={anchorRef?.current}
                  open={isOpenFavouritesDialog}
                  placement='bottom-start'
                  sx={{
                    zIndex: theme => theme.zIndex.tooltip,
                  }}
                >
                  <ClickAwayListener
                    mouseEvent='onMouseDown'
                    onClickAway={handleToggleFavouritesDialog}
                  >
                    <Box
                      sx={{
                        mt: '4px',
                        ml: '-4px',
                        width: 280,
                        // borderRadius: 0,
                        backgroundColor: theme => theme.palette.background.paper,
                        boxShadow: theme => theme.palette.boxShadow.paper,
                        border: theme => theme.palette.border.field,
                      }}
                    >
                      {isPendingSearchFilters ? (
                        <>
                          <AppLoader size='1.5rem' sx={{ my: 0.5 }} />
                          <Grid sx={{ p: '0 4px 0 50px' }}>
                            <Divider sx={{ borderColor: theme => theme.palette.color.divider }} />
                          </Grid>
                        </>
                      ) : (
                        <>
                          {savedSearchFilters?.map(search => (
                            <ListItem
                              key={search.id}
                              selected={search.id === selectedFavouriteCriteria?.id}
                              title={search.title}
                              onClick={handleSelected(search)}
                            />
                          ))}
                          {Boolean(savedSearchFilters?.length) && (
                            <Grid sx={{ p: '0 4px 0 50px' }}>
                              <Divider sx={{ borderColor: theme => theme.palette.color.divider }} />
                            </Grid>
                          )}
                        </>
                      )}
                      <ListItem
                        disabled={!isDirtyVisibleCriterias || !selectedFavouriteCriteria?.id}
                        title={t('filter.favorites.save')}
                        onClick={handleClickSave}
                      />
                      <ListItem
                        disabled={!isDirtyVisibleCriterias}
                        title={t('filter.favorites.saveAs')}
                        onClick={handleClickSaveAs}
                      />
                      <Grid sx={{ p: '0 4px 0 50px' }}>
                        <Divider sx={{ borderColor: theme => theme.palette.color.divider }} />
                      </Grid>
                      <ListItem title={t('filter.favorites.manage')} onClick={handleClickManage} />
                    </Box>
                  </ClickAwayListener>
                </Popper>
              </Grid>
              <Grid item>
                <Button
                  disabled={!isDirtyVisibleCriterias}
                  sx={theme => buttonStylesByMode(theme, true)}
                  variant='text'
                  onClick={handleReset}
                >
                  {t('filter.reset')}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  sx={theme => buttonStylesByMode(theme, true)}
                  variant='text'
                  onClick={handleSearch}
                >
                  {t('filter.search')}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )
    }

    return (
      <SearchAssistantProvider
        editable={false}
        entityInternalId={entityInternalId}
        entityObjectCode={entityObjectCode}
        isDropDownWindowOpen={isDropDownWindowOpen}
        isInDialogWindow={isInDialogWindow}
        searchAssistantId={searchAssistantId}
        selectedFavourite={null /*selectedFavouriteCriterias */}
        onChangeDropDownWindowOpen={setDropDownWindowOpen}
        onSearchFilter={onSearchFilter}
      >
        <ClickAwayListener mouseEvent={'onMouseDown'} onClickAway={handleClickAway}>
          <Grid
            container
            bgcolor={theme => theme.palette.background.main}
            visibility={visible ? 'visible' : 'hidden'}
            onClick={handleClickInside}
          >
            <Grid item lg md sm xs>
              <SearchAssistantAccordion
                disabled={isPendingSearchFilters}
                expanded={isOpenFilter}
                loading={isPendingSearchFilters}
                searchAssistantId={searchAssistantId}
                title={t('filter.create')}
                onChange={handleOpenFilter}
              >
                <PageContentLayout
                  sx={{
                    '&.MuiPaper-root': {
                      padding: 0,
                      '&.MuiPaper-root.MuiPaper-rounded': { borderRadius: 0 },
                    },
                  }}
                >
                  <Grid item>{children}</Grid>
                  {getContent()}
                </PageContentLayout>
              </SearchAssistantAccordion>
            </Grid>
          </Grid>
        </ClickAwayListener>
      </SearchAssistantProvider>
    )
  }
)

SearchAssistant.displayName = 'SearchAssistant'

export { SearchAssistant }
